import type { CreateRoomInput, FrameCheckResult, SessionInfo } from '@vybe/shared';

/** Empty means same origin (production bundle served by the server, or the Vite dev proxy). */
export const SERVER_URL =
  (import.meta.env.VITE_SERVER_URL as string | undefined)?.replace(/\/$/, '') ?? '';

/**
 * Split deploys (e.g. a free PaaS that sleeps when idle) can take tens of seconds to wake up.
 * One long health ping runs at startup and regular requests wait for it, so the first action
 * after a cold start does not fail on the normal 10 s timeout.
 */
let warmUp: Promise<void> = Promise.resolve();

export function warmUpServer(): void {
  if (!SERVER_URL) return;
  warmUp = fetch(`${SERVER_URL}/api/health`, {
    credentials: 'omit',
    signal: AbortSignal.timeout(90_000),
  }).then(
    () => undefined,
    () => undefined,
  );
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
  ) {
    super(code);
  }
}

async function request<T>(
  path: string,
  init: RequestInit & { token?: string | null } = {},
): Promise<T> {
  await warmUp;
  const headers = new Headers(init.headers);
  if (init.body) headers.set('content-type', 'application/json');
  if (init.token) headers.set('authorization', `Bearer ${init.token}`);
  const response = await fetch(`${SERVER_URL}${path}`, {
    ...init,
    headers,
    credentials: 'omit',
    signal: init.signal ?? AbortSignal.timeout(10_000),
  });
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const code =
      typeof body === 'object' && body !== null && 'error' in body && typeof body.error === 'string'
        ? body.error
        : 'unknown';
    throw new ApiError(response.status, code);
  }
  return body as T;
}

export const api = {
  createSession: (displayName: string) =>
    request<{ sessionToken: string; session: SessionInfo }>('/api/sessions', {
      method: 'POST',
      body: JSON.stringify({ displayName }),
    }),
  me: (token: string) => request<{ session: SessionInfo }>('/api/sessions/me', { token }),
  rename: (token: string, displayName: string) =>
    request<{ session: SessionInfo }>('/api/sessions/me', {
      method: 'PATCH',
      token,
      body: JSON.stringify({ displayName }),
    }),
  createRoom: (token: string, input: CreateRoomInput) =>
    request<{ roomId: string; inviteToken: string }>('/api/rooms', {
      method: 'POST',
      token,
      body: JSON.stringify(input),
    }),
  roomPreview: (roomId: string, inviteToken?: string) =>
    request<{
      roomId: string;
      isPrivate: boolean;
      name: string | null;
      participantCount: number | null;
      maxParticipants: number;
    }>(
      `/api/rooms/${encodeURIComponent(roomId)}${inviteToken ? `?t=${encodeURIComponent(inviteToken)}` : ''}`,
    ),
  /** Server-side read of a page's framing headers (X-Frame-Options / frame-ancestors). */
  frameCheck: (token: string, url: string, origin: string) =>
    request<FrameCheckResult>(
      `/api/frame-check?url=${encodeURIComponent(url)}&origin=${encodeURIComponent(origin)}`,
      { token },
    ),
  iceServers: (token: string) => request<{ iceServers: RTCIceServer[] }>('/api/ice', { token }),
};
